import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import I18n from 'utils/I18n';
import { trackV2 } from 'utils/analytics';
import clsx from '@strava/ui/clsx';
import { camelCase, upperFirst } from 'lodash-es';
import styles from './LandingPage.scss';
import { LOCALE } from '../../constants/appConstants';
import Header from './components/Header';
import Footer from './components/Footer';
import Microfrontend from '../Microfrontend/Microfrontend';
import AccountSignupFormV2 from './components/AccountSignupFormV2';

const COLUMN_SIDE = Object.freeze({
  left: 'left',
  right: 'right'
});

const LandingPage = ({
  locale,
  config,
  authorizationUrls,
  flashMessage,
  promotionalBannerOptions,
  cpraComplianceProps,
  honeyPot,
  reCaptchaSiteKey,
  isPremium,
  formAuthToken
}) => {
  const {
    url,
    scope,
    component,
    appContext,
    showPromotionBanner
  } = promotionalBannerOptions;

  // default to en-US for all untranslated locales
  const languageAlias = Object.values(LOCALE).includes(locale)
    ? locale
    : LOCALE.enUS;

  useEffect(() => {
    trackV2({
      category: 'onboarding',
      page: 'home',
      action: 'screen_enter',
      properties: {
        source: 'v2',
        locale,
        cohort: 'control'
      }
    });
  }, [locale]);

  // computes the class for side col images
  // eg. derives 'leftImageEnUs' from side - left and locale - en-US
  const bgImgClass = (side) =>
    `${side}Image${upperFirst(camelCase(languageAlias))}`;

  return (
    <div className={styles.container}>
      <Header />
      {showPromotionBanner && (
        <div className={styles.promotionalBanner}>
          <Microfrontend
            url={url}
            scope={scope}
            component={component}
            appContext={appContext}
          />
        </div>
      )}
      <div className={styles.mainContent}>
        <div
          data-cy="left-column"
          className={clsx(
            styles.sideColumn,
            styles.positionTopRight,
            styles[bgImgClass(COLUMN_SIDE.left)]
        )}
        />
        <div className={styles.middleColumn}>
          <h2>{I18n.t('components.landing_page.heading_v2')}</h2>
          <p>{I18n.t('components.landing_page.description')}</p>

          <AccountSignupFormV2
            config={config}
            authorizationUrls={authorizationUrls}
            flashMessage={flashMessage}
            honeyPot={honeyPot}
            reCaptchaSiteKey={reCaptchaSiteKey}
            isPremium={isPremium}
            formAuthToken={formAuthToken}
          />
        </div>
        <div
          data-cy="right-column"
          className={clsx(
            styles.sideColumn,
            styles.positionTopLeft,
            styles[bgImgClass(COLUMN_SIDE.right)]
          )}
        />
      </div>
      <Footer cpraComplianceProps={cpraComplianceProps} />
    </div>
  );
};

LandingPage.defaultProps = {
  flashMessage: null,
  locale: LOCALE.enUS,
  promotionalBannerOptions: {
    showPromotionBanner: false
  },
  experiment: {
    cohort: 'control'
  }
};

LandingPage.propTypes = {
  locale: PropTypes.string,
  config: PropTypes.shape({
    appleEnabled: PropTypes.bool.isRequired,
    appleState: PropTypes.string.isRequired // https://developer.apple.com/documentation/sign_in_with_apple/clientconfigi/3230956-state
  }).isRequired,
  authorizationUrls: PropTypes.shape({
    google: PropTypes.string.isRequired,
    facebook: PropTypes.string.isRequired
  }).isRequired,
  flashMessage: PropTypes.string,
  promotionalBannerOptions: PropTypes.shape({
    url: PropTypes.string,
    scope: PropTypes.string,
    component: PropTypes.string,
    appContext: PropTypes.shape({
      promoType: PropTypes.string,
      promoCode: PropTypes.string,
      redirectUrl: PropTypes.string,
      analyticsTrackingData: PropTypes.shape({
        action: PropTypes.string,
        element: PropTypes.string,
        category: PropTypes.string,
        page: PropTypes.string
      })
    }),
    showPromotionBanner: PropTypes.bool
  }),
  cpraComplianceProps: PropTypes.shape({
    url: PropTypes.string,
    scope: PropTypes.string,
    component: PropTypes.string,
    appContext: PropTypes.shape({})
  }).isRequired,
  experiment: PropTypes.shape({
    cohort: PropTypes.string.isRequired
  }),
  honeyPot: PropTypes.string.isRequired,
  isPremium: PropTypes.bool.isRequired,
  reCaptchaSiteKey: PropTypes.string.isRequired,
  formAuthToken: PropTypes.string.isRequired
};

export default LandingPage;
